import moment from 'moment';
import React, { useEffect, useState } from 'react';

import { AppointmentStatus, PhoneType } from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import PatientGuideRelationship from '@vestahealthcare/common/enums/PatientGuideRelationship';
import { translate } from '@vestahealthcare/common/i18n';
import { Patient, PatientGuideDetails } from '@vestahealthcare/common/models';
import { DATE_FORMAT } from '@vestahealthcare/common/utils/constants';
import { validate } from '@vestahealthcare/common/utils/phone';

import {
  Button,
  CollapsableSidebar,
  DateTimePicker,
  PhoneInput,
  Select,
  TextInput,
} from 'styleguide-v2';

import { showGlobalError } from 'dash/src/components/GlobalMessage';
import {
  createPatientGuideDetails,
  updatePatientGuideDetails,
} from 'dash/src/services/PatientServices';

interface EditGuideDetailsProps {
  patient: Patient;
  guideDetails?: PatientGuideDetails;
  onSubmit: () => Promise<void>;
  open: boolean;
  onClose: () => void;
}

export const EditGuideDetails = React.memo((props: EditGuideDetailsProps) => {
  const { patient, guideDetails, onSubmit, open, onClose } = props;

  const [loading, setLoading] = useState<boolean>(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneType, setPhoneType] = useState<Selectable | undefined>();
  const [relationship, setRelationship] = useState<Selectable | undefined>();
  const [livesWithMember, setLivesWithMember] = useState(false);
  const [initiatingVisitDate, setInitiatingVisitDate] = useState<
    Date | undefined
  >();
  const [visitStatus, setVisitStatus] = useState<Selectable | undefined>();

  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (guideDetails && open) {
      setFirstName(guideDetails.firstName || '');
      setLastName(guideDetails.lastName || '');
      setPhoneNumber(guideDetails.phoneNumber || '');
      setPhoneType(PhoneType.getPhoneTypeFromString(guideDetails.phoneType));
      setRelationship(
        PatientGuideRelationship.getRelationshipFromString(
          guideDetails?.relationship,
        ),
      );
      setLivesWithMember(guideDetails.livesWithMember || false);
      setInitiatingVisitDate(
        guideDetails.initiatingVisit
          ? guideDetails.initiatingVisit.toDate()
          : undefined,
      );
      setVisitStatus(
        AppointmentStatus.getStatusFromString(guideDetails?.visitStatus),
      );
    }
    if (!open) {
      setSubmitted(false);
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setPhoneType(undefined);
      setRelationship(undefined);
      setLivesWithMember(false);
      setInitiatingVisitDate(undefined);
      setVisitStatus(undefined);
    }

    setLoading(false);
  }, [open, guideDetails]);

  const handleSubmit = async (): Promise<boolean> => {
    setSubmitted(true);

    if (
      !firstName ||
      !lastName ||
      !phoneNumber ||
      !phoneType ||
      !relationship ||
      !initiatingVisitDate ||
      !visitStatus ||
      (phoneNumber && !validate(phoneNumber)) ||
      (initiatingVisitDate && !moment(initiatingVisitDate).isValid())
    ) {
      return false;
    }

    setLoading(false);

    try {
      const { isEmpty, ...updateData } = {
        ...guideDetails,
        firstName,
        lastName,
        phoneNumber,
        phoneType: phoneType?.value as string,
        relationship: relationship?.value as string,
        livesWithMember,
        initiatingVisitDate: moment(initiatingVisitDate).format(DATE_FORMAT),
        visitStatus: visitStatus?.value as string,
      };

      if (!isEmpty) {
        await updatePatientGuideDetails(
          patient.id,
          (updateData as unknown) as PatientGuideDetails,
        );
      } else {
        await createPatientGuideDetails(
          patient.id,
          (updateData as unknown) as PatientGuideDetails,
        );
      }

      await onSubmit();
      setSubmitted(false);
      setLoading(false);
      return true;
    } catch (e) {
      showGlobalError(e as string);
      setLoading(false);
      return false;
    }
  };

  const hasServerPhoneError = !!(phoneNumber && !validate(phoneNumber));

  return (
    <CollapsableSidebar
      open={open}
      onClose={onClose}
      title={translate('personalDetails.guideDetails.title')}
      size={550}
    >
      <CollapsableSidebar.Body>
        <div className="grid-wrapper fit">
          <TextInput
            className="grid-span-6"
            data-cy="guide-contact-first-name"
            error={submitted && !firstName}
            label={translate('personalDetails.guideDetails.firstName')}
            onChange={(value?: string) => setFirstName(value || '')}
            placeholder={translate(
              'personalDetails.guideDetails.enterFirstName',
            )}
            value={firstName}
            required
          />

          <TextInput
            className="grid-span-6"
            data-cy="guide-contact-last-name"
            error={submitted && !lastName}
            label={translate('personalDetails.guideDetails.lastName')}
            onChange={(value?: string) => setLastName(value || '')}
            placeholder={translate(
              'personalDetails.guideDetails.enterLastName',
            )}
            value={lastName}
            required
          />

          <PhoneInput
            className="grid-span-6"
            data-cy="guide-contact-phone"
            label={translate('personalDetails.guideDetails.phoneNumber')}
            onChange={(value?: string) => setPhoneNumber(value || '')}
            submitted={submitted || hasServerPhoneError}
            value={phoneNumber}
            required
            placeholder={translate(
              'personalDetails.guideDetails.enterPhoneNumber',
            )}
          />

          <Select
            className="grid-span-6"
            data-cy="guide-contact-phone-type"
            error={submitted && !phoneType}
            label={translate('personalDetails.guideDetails.phoneType')}
            onChange={setPhoneType}
            placeholder={translate(
              'personalDetails.guideDetails.choosePhoneType',
            )}
            items={PhoneType.toSelectable().filter(
              (type) =>
                type.value === PhoneType.HOME.value ||
                type.value === PhoneType.CELL.value,
            )}
            value={phoneType}
            required
          />

          <Select
            className="grid-span-6"
            data-cy="guide-contact-relationship"
            error={submitted && !relationship}
            label={translate('personalDetails.guideDetails.relationship')}
            onChange={setRelationship}
            placeholder={translate(
              'personalDetails.guideDetails.chooseRelationship',
            )}
            items={PatientGuideRelationship.toSelectable()}
            value={relationship}
            required
          />

          <Select.Choice
            className="grid-span-6"
            data-cy="guide-contact-lives-with-member"
            label={translate('personalDetails.guideDetails.livesWithMember')}
            onChange={(value?: boolean) => setLivesWithMember(value || false)}
            value={livesWithMember}
            disableClearable
            required
          />
          <DateTimePicker
            className="grid-span-6"
            data-cy="guide-contact-initiating-visit"
            error={
              submitted &&
              (!initiatingVisitDate ||
                (initiatingVisitDate &&
                  !moment(initiatingVisitDate).isValid() &&
                  translate('personalDetails.guideDetails.invalidDate')))
            }
            label={translate('personalDetails.guideDetails.initiatingVisit')}
            onChange={setInitiatingVisitDate}
            placeholder={translate('personalDetails.guideDetails.enterDate')}
            value={initiatingVisitDate}
            required
          />
          <Select
            className="grid-span-6"
            data-cy="guide-contact-visit-status"
            error={submitted && !visitStatus}
            label={translate('personalDetails.guideDetails.visitStatus')}
            onChange={setVisitStatus}
            placeholder={translate(
              'personalDetails.guideDetails.chooseVisitStatus',
            )}
            items={AppointmentStatus.toSelectable()}
            value={visitStatus}
            required
          />
        </div>
      </CollapsableSidebar.Body>
      <CollapsableSidebar.Buttons>
        <Button color="tertiary" onClick={onClose}>
          {translate('global.close')}
        </Button>
        <Button color="secondary" loading={loading} onClick={handleSubmit}>
          {translate('global.save')}
        </Button>
      </CollapsableSidebar.Buttons>
    </CollapsableSidebar>
  );
});
